import type { VaultFile } from "../types";

const MEBIBYTE = 1024 * 1024;
const MAX_EXPORT_FILES = 50;
const WEB_EXPORT_BYTES = 100 * MEBIBYTE;
const NATIVE_EXPORT_BYTES = 25 * MEBIBYTE;
const FILE_DOWNLOAD_TIMEOUT_MS = 60_000;
// eslint-disable-next-line no-control-regex -- Reject control bytes in untrusted filenames.
const UNSAFE_FILENAME_CHARACTERS = /[\\/:*?"<>|\u0000-\u001f\u007f]/g;

export type VaultDownloadLimits = { maxFiles: number; maxTotalBytes: number };
export type DownloadedVaultFile = { file: VaultFile; blob: Blob };
export type VaultDownloadFailure = { file: VaultFile; message: string };

export function vaultDownloadLimits(isNative: boolean): VaultDownloadLimits {
  return {
    maxFiles: MAX_EXPORT_FILES,
    maxTotalBytes: isNative ? NATIVE_EXPORT_BYTES : WEB_EXPORT_BYTES,
  };
}

export function uniqueVaultFiles(files: VaultFile[]): VaultFile[] {
  return [...new Map(files.map((file) => [file.id, file])).values()];
}

export function validateVaultDownload({
  files,
  limits,
}: {
  files: VaultFile[];
  limits: VaultDownloadLimits;
}): void {
  if (files.length === 0) {
    throw new Error("Select at least one file to download.");
  }
  if (files.some((file) => !file.canDownload)) {
    throw new Error(
      "Some selected files are not available to download. Refresh the list and try again.",
    );
  }
  if (files.length > limits.maxFiles) {
    throw new Error(`Choose up to ${limits.maxFiles} files per download.`);
  }
  if (
    files.reduce((total, file) => total + file.fileSizeBytes, 0) >
    limits.maxTotalBytes
  ) {
    throw new Error(
      `Choose a smaller batch, up to ${limits.maxTotalBytes / MEBIBYTE} MB per download.`,
    );
  }
}

/** Storage filenames are display data, never paths on the device or in a ZIP. */
export function vaultDownloadName(name: string): string {
  const sanitized = name.replace(UNSAFE_FILENAME_CHARACTERS, "_").trim();
  if (!sanitized || sanitized === "." || sanitized === "..") {
    return "document";
  }
  return sanitized;
}

function archiveNames(files: VaultFile[]): Map<string, string> {
  const used = new Set<string>();
  const names = new Map<string, string>();
  // Sorting by ID makes collision suffixes stable regardless of selection order.
  for (const file of [...files].sort((left, right) =>
    left.id.localeCompare(right.id),
  )) {
    const original = vaultDownloadName(file.fileName);
    const extensionIndex = original.lastIndexOf(".");
    const stem =
      extensionIndex > 0 ? original.slice(0, extensionIndex) : original;
    const extension = extensionIndex > 0 ? original.slice(extensionIndex) : "";
    let name = original;
    let collision = 0;
    while (used.has(name.normalize("NFC").toLowerCase())) {
      collision += 1;
      name = `${stem} (${file.id}-${collision})${extension}`;
    }
    used.add(name.normalize("NFC").toLowerCase());
    names.set(file.id, name);
  }
  return names;
}

export async function createVaultArchive(
  files: DownloadedVaultFile[],
): Promise<Blob> {
  const unique = [
    ...new Map(files.map((entry) => [entry.file.id, entry])).values(),
  ];
  const names = archiveNames(unique.map((entry) => entry.file));
  // Loaded on demand so zip.js stays out of the entry bundle.
  const { BlobReader, BlobWriter, ZipWriter } = await import("@zip.js/zip.js");
  // PDFs and images are already compressed. Store them without recompression
  // to keep archive creation responsive and avoid worker setup on native apps.
  const writer = new ZipWriter(new BlobWriter("application/zip"), {
    level: 0,
    useWebWorkers: false,
  });
  for (const entry of unique) {
    await writer.add(names.get(entry.file.id)!, new BlobReader(entry.blob));
  }
  return writer.close();
}

async function readBoundedBlob({
  response,
  maxBytes,
}: {
  response: Response;
  maxBytes: number;
}): Promise<Blob> {
  const tooLarge = () =>
    new Error(
      "This file exceeds the remaining download size limit. Download it in a smaller batch.",
    );
  const declaredSize = Number(response.headers.get("content-length"));
  if (declaredSize > maxBytes) {
    await response.body?.cancel();
    throw tooLarge();
  }
  if (!response.body) {
    const blob = await response.blob();
    if (blob.size > maxBytes) {
      throw tooLarge();
    }
    return blob;
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let total = 0;
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) {
        break;
      }
      total += chunk.value.byteLength;
      if (total > maxBytes) {
        await reader.cancel();
        throw tooLarge();
      }
      chunks.push(new Uint8Array(chunk.value));
    }
  } finally {
    reader.releaseLock();
  }
  return new Blob(chunks, {
    type: response.headers.get("content-type") ?? "application/octet-stream",
  });
}

export async function fetchVaultFile({
  file,
  mintUrl,
  maxBytes,
  signal,
}: {
  file: VaultFile;
  mintUrl: (file: VaultFile) => Promise<string>;
  maxBytes: number;
  signal: AbortSignal;
}): Promise<DownloadedVaultFile> {
  signal.throwIfAborted();
  // Mint on every attempt, including retries. The API rechecks view and scan
  // access; neither cached inventory data nor an expired link grants access.
  const url = await mintUrl(file);
  signal.throwIfAborted();
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(abort, FILE_DOWNLOAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      credentials: "omit",
    });
    if (!response.ok) {
      throw new Error("Could not retrieve this file. Please retry.");
    }
    return { file, blob: await readBoundedBlob({ response, maxBytes }) };
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", abort);
  }
}

export async function collectVaultFiles({
  files,
  initialFiles = [],
  limits,
  mintUrl,
  signal,
  onProgress,
}: {
  files: VaultFile[];
  initialFiles?: DownloadedVaultFile[];
  limits: VaultDownloadLimits;
  mintUrl: (file: VaultFile) => Promise<string>;
  signal: AbortSignal;
  onProgress: (progress: { completed: number; total: number }) => void;
}): Promise<{
  successful: DownloadedVaultFile[];
  failures: VaultDownloadFailure[];
}> {
  const successful = [...initialFiles];
  const failures: VaultDownloadFailure[] = [];
  const remaining = uniqueVaultFiles(files).filter(
    (file) => !successful.some((entry) => entry.file.id === file.id),
  );
  validateVaultDownload({
    files: [...successful.map((entry) => entry.file), ...remaining],
    limits,
  });
  let bytes = successful.reduce((total, entry) => total + entry.blob.size, 0);
  onProgress({ completed: 0, total: remaining.length });
  // Fetch sequentially so the byte cap is shared across the whole batch and
  // mobile never buffers several maximum-size network responses at once.
  for (const [index, file] of remaining.entries()) {
    signal.throwIfAborted();
    try {
      const entry = await fetchVaultFile({
        file,
        mintUrl,
        maxBytes: limits.maxTotalBytes - bytes,
        signal,
      });
      bytes += entry.blob.size;
      successful.push(entry);
    } catch (error) {
      signal.throwIfAborted();
      failures.push({
        file,
        message:
          error instanceof Error
            ? error.message
            : "Could not download this file.",
      });
    }
    onProgress({ completed: index + 1, total: remaining.length });
  }
  return { successful, failures };
}
